import type { GettextInterface } from "@indeed/indeed-gettext";

import type {
  HiringEventGenericOnlineLocation,
  HiringEventPhoneLocation,
  HiringEventPhysicalLocation,
  HiringEventVipLocation,
} from "../../../../../../../../__generated__/types.gqlgenerated";
import type { HiringEventData } from "../../../../../../hooks/useConditionalHiringEventsData";
import { LOCALES_EXPECTING_TIMEZONES, LocationTypeName } from "../../../constants/apiConstants";
import { validTimezoneOrDefault } from "../../../utils/dateUtils";
import { formatShortDateTimeRange, getTimezoneName } from "../../../utils/formatUtils";
import { ObjectUtils } from "../../../utils/objectUtils";
import type { HiringEventsViewJobOverviewProps } from "../index";
import type { RemoteLocationType } from "../LocationSection";

type HiringEventLocation =
  | HiringEventPhysicalLocation
  | HiringEventVipLocation
  | HiringEventPhoneLocation
  | HiringEventGenericOnlineLocation;

export const hiringEventsViewJobOverviewDataAdapter = (
  gt: GettextInterface,
  hiringEvent: HiringEventData,
): Omit<HiringEventsViewJobOverviewProps, "jobKey"> => {
  const {
    eventLocale,
    interviewConfig: {
      schedulingConfig: { useInterviewSlots, timeSlotDurationMs },
      availability: { timePeriod: timePeriods },
    },
    posting: { interviewsOffered },
  } = hiringEvent;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const location = hiringEvent.interviewConfig.location as HiringEventLocation;

  const timezone = validTimezoneOrDefault(location.timezone, "America/Chicago");
  const now = Date.now();
  const dateTimes = timePeriods
    .map((timePeriod) => ({
      timestamp: new Date(timePeriod.startTime).getTime(),
      startTime: timePeriod.startTime,
      endTime: timePeriod.endTime,
    }))
    .filter((timePeriod) => now <= timePeriod.timestamp)
    .sort((t1, t2) => t1.timestamp - t2.timestamp)
    .map((timePeriod) =>
      formatShortDateTimeRange(timePeriod.startTime, timePeriod.endTime, eventLocale, timezone),
    );

  const timezoneLabel = LOCALES_EXPECTING_TIMEZONES.has(eventLocale)
    ? timePeriods
        .flatMap((timePeriod) => [
          getTimezoneName(timePeriod.startTime, eventLocale, timezone),
          getTimezoneName(timePeriod.endTime, eventLocale, timezone),
        ])
        .filter((tz, index, arr) => arr.indexOf(tz) === index)
        .sort()
        .join(" / ")
    : "";

  return {
    dateTimes,
    estimatedDuration:
      useInterviewSlots && timeSlotDurationMs
        ? gt.npgettext(
            "Displaying duration as string",
            "1 minute",
            "{0} minutes",
            timeSlotDurationMs,
            [timeSlotDurationMs],
          )
        : "",
    hiringEventUuid: hiringEvent.id,
    interviewsOffered,
    location:
      location.__typename === LocationTypeName.PHYSICAL_LOCATION_TYPE_NAME
        ? {
            locationType: LocationTypeName.PHYSICAL_LOCATION_TYPE_NAME,
            address: location.formattedLocation,
          }
        : {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
            locationType: ObjectUtils.defaultIfAbsent(
              LocationTypeName,
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
              location.__typename as LocationTypeName,
              LocationTypeName.GENERIC_ONLINE_LOCATION_TYPE_NAME,
            ) as RemoteLocationType,
          },
    timezone: timezoneLabel,
  };
};
